import { RoleTypeEnum } from "constants/enums/RoleEnum";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getSelectedAuthorization } from "store/slices/userSlice";
import { getUserStudents } from "store/slices/userStudentsSlice";

const useStudentServiceParams = (fields) => {
  const [params, setParams] = useState({});
  const students = useSelector(getUserStudents);
  const auth = useSelector(getSelectedAuthorization);
  const user = useSelector((state) => state?.user);

  useEffect(() => {
    if (fields && typeof fields === "object" && Object.keys(fields).length > 0) {
      const result = {};
      Object.keys(fields).forEach((param) => {
        switch (param) {
          case "termId":
            result.termId = getTermId();
            break;
          case "termName":
            result.termName = getTermName();
            break;
          case "schoolId":
            result.schoolId = getSchoolId();
            break;
          case "levelId":
            result.levelId = getLevelId();
            break;
          case "levelCode":
            result.levelCode = getLevelCode();
            break;
          case "levelInfo":
            result.levelInfo = getStudentLevelInfo();
            break;
          case "roleId":
            result.roleId = getRoleId();
            break;
          case "classInfo":
            result.classInfo = getClassInfo();
            break;
          case "classId":
            result.classId = getClassInfo()?.id;
            break;
          case "studentRoleId":
            result.studentRoleId = getStudentRoleId();
            break;
          case "schoolInfo":
            result.schoolInfo = getSchoolInfo();
            break;
          case "corporationInfo":
            result.corporationInfo = getCorporationInfo();
            break;
          case "corporationId":
            result.corporationId = getCorporationInfo()?.id;
            break;
          case "studentInfo":
            result.studentInfo = getStudentInfo();
            break;
          case "permCode":
            result.permCode = getPermCode();
            break;
          case "studentDetails":
            result.studentDetails = getStudentDetails();
            break;
          case "groups":
            result.groups = getStudentGroups();
            break;
          case "integration":
            result.integration = getStudentIntegrations();
            break;
          case "studentField":
            result.studentField = getStudentField();
            break;
          case "studentName":
            result.studentName = getStudentName();
            break;
          case "corporationInfoDetails":
            result.corporationInfoDetails = getCorporationInfoDetails();
            break;

          default:
            result[param] = "";
            break;
        }
      });
      setParams(result);
    }
  }, [students, auth, user]);

  const getStudentField = () => {
    if (auth?.roleType === RoleTypeEnum.STUDENT) return auth?.details?.field;
    else {
      const _student = students.find(
        (student) => student.roleId === auth?.details?.studentUserRoleId
      );
      return _student?.studentDetails?.field;
    }
  };

  const getTermId = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.termInfo?.id
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.termInfo?.id;
  };

  const getStudentName = () => {
    if (auth?.roleType === RoleTypeEnum.STUDENT)
      return `${user?.identity?.name} ${user?.identity?.surname}`;
    else {
      const _student = students.find(
        (student) => student.roleId === auth?.details?.studentUserRoleId
      );
      return `${_student?.identity?.name} ${_student?.identity?.surname}`;
    }
  };

  const getTermName = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.termInfo?.name
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.termInfo?.name;
  };

  const getSchoolId = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.schoolInfo?.id
      : students?.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.schoolInfo?.id;
  };

  const getSchoolInfo = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.schoolInfo
      : students?.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.schoolInfo;
  };

  const getCampusInfo = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.campusInfo
      : students?.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.campusInfo;
  };

  const getCorporationInfo = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.corporationInfo
      : students?.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.corporationInfo;
  };

  const getLevelId = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.levelInfo?.id
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.levelInfo?.id;
  };

  const getLevelCode = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.levelInfo?.code
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.levelInfo?.code;
  };

  const getRoleId = () => {
    return auth?.roleType === RoleTypeEnum.STUDENT
      ? auth?.id
      : students?.find((student) => student.roleId === auth?.details?.studentUserRoleId)?.roleId;
  };

  const getClassInfo = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.classInfo
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.classInfo || null;
  };

  const getStudentInfo = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? user
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId) || null;
  };

  const getStudentDetails = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? user?.selectedAuthorization?.details
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails || null;
  };

  const getPermCode = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? user?.selectedAuthorization?.permCode
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)?.permCode ||
          null;
  };

  const getStudentRoleId = () => {
    return (
      (auth.roleType === RoleTypeEnum.STUDENT ? auth.id : auth?.details?.studentUserRoleId) || ""
    );
  };

  const getStudentGroups = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? user?.selectedAuthorization?.groups
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)?.groups ||
          null;
  };

  const getStudentIntegrations = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.integration || []
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.integration || [];
  };

  const getStudentLevelInfo = () => {
    return auth.roleType === RoleTypeEnum.STUDENT
      ? auth?.details?.levelInfo
      : students.find((student) => student.roleId === auth?.details?.studentUserRoleId)
          ?.studentDetails?.levelInfo || null;
  };

  const getCorporationInfoDetails = () => {
    const corp = getCorporationInfo();
    const sch = getSchoolInfo();
    const camp = getCampusInfo();

    const _corporationInfo = {
      schoolId: sch?.id,
      schoolName: sch?.name,
      schoolType: "",
      campusId: camp?.id,
      campusName: camp?.name,
      corporationId: corp?.id,
      corporationName: corp?.name
    };

    return _corporationInfo;
  };

  return { params };
};

export default useStudentServiceParams;
